import { Fragment } from "react"
import { Divider, FormControlLabel, Stack, Switch, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useApiMutation } from "../../../core/api/useApiMutation"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { parseBool } from "../../../lib/lang"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { translations } from "../../localization/translations"
import type { TranslationKey } from "../../localization/translations"
import { getUserDetails, saveAdminUserSettings, UserQuery } from "../../users/userApi"
import type { GetUserDetailsResponse } from "../../users/userApi"
import { UserSettingElementType } from "../../users/userTypes"
import type { UserSetting } from "../../users/userTypes"

// push is per device, the app has its own screen for it
const PUSH_SETTING_PREFIX = "ACCEPT_PUSH_"
const LEGACY_RECEIPTS_SETTING = "ORDER_EMAIL_RECEIPT"
const RECEIPT_TOGGLES = [
    { name: "ORDER_EMAIL_RECEIPT_MEETING_SERVICE", translationKey: "settings-receipts-meeting-service" },
    { name: "ORDER_EMAIL_RECEIPT_CANTEEN", translationKey: "settings-receipts-canteen" },
]

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[600],
    },
    titleDivider: {
        marginTop: ".75rem",
    },
    row: {
        padding: ".5rem 0",
        "&.MuiFormControlLabel-root": {
            margin: 0,
            justifyContent: "space-between",
        },
    },
}))

function isTranslationKey(key: string): key is TranslationKey {
    return key in translations["en-US"]
}

const isLegacyReceiptToggle = ({ name }: UserSetting) => name === LEGACY_RECEIPTS_SETTING
const isReceiptToggle = ({ name }: UserSetting) => RECEIPT_TOGGLES.some((toggle) => toggle.name === name)

function splitReceiptToggles(settings: ReadonlyArray<UserSetting>): ReadonlyArray<UserSetting> {
    const source = settings.find(isLegacyReceiptToggle) ?? settings.find(isReceiptToggle)
    if (!source) return settings

    // saved ones come back without elementType; unsaved ones start from the old toggle
    const receipts = RECEIPT_TOGGLES.map((toggle) => ({
        ...toggle,
        elementType: UserSettingElementType.TOGGLE,
        value: settings.find(({ name }) => name === toggle.name)?.value ?? source.value,
    }))

    return [...receipts, ...settings.filter((setting) => !isLegacyReceiptToggle(setting) && !isReceiptToggle(setting))]
}

const selectNotificationToggles = ({ user }: GetUserDetailsResponse) =>
    splitReceiptToggles(user.userSettings ?? []).filter(
        (setting) =>
            setting.elementType === UserSettingElementType.TOGGLE &&
            Boolean(setting.translationKey) &&
            !setting.name.startsWith(PUSH_SETTING_PREFIX),
    )

function NotificationToggle({ setting }: Readonly<{ setting: UserSetting }>) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const { mutate, isLoading, variables } = useApiMutation(saveAdminUserSettings, {
        async onSuccess(_, __, { queryClient }) {
            await queryClient.invalidateQueries({ queryKey: [UserQuery.USER_DETAILS] })
        },
    })

    const value = isLoading && variables ? variables.userSettings.value : setting.value
    const { translationKey, name, elementType } = setting

    function handleChange(checked: boolean) {
        mutate({ userSettings: { name, elementType, value: checked.toString() } })
    }

    return (
        <FormControlLabel
            className={classes.row}
            labelPlacement="start"
            label={translationKey && isTranslationKey(translationKey) ? l10n.getString(translationKey) : name}
            control={
                <Switch
                    checked={parseBool(value) ?? false}
                    disabled={isLoading}
                    onChange={(_, checked) => handleChange(checked)}
                />
            }
        />
    )
}

export function UserNotificationSettings({ userId }: Readonly<{ userId: number }>) {
    const { classes } = useStyles()
    const { data: toggles } = useApiQuery(getUserDetails, {
        queryName: UserQuery.USER_DETAILS,
        dependencies: { userId },
        select: selectNotificationToggles,
    })

    if (!toggles?.length) return null

    return (
        <Stack>
            <LocalizedStrict id="settings-notifications-label">
                <Typography variant="subtitle1" className={classes.title}>
                    Notifications
                </Typography>
            </LocalizedStrict>
            <Divider className={classes.titleDivider} />
            {toggles.map((setting) => (
                <Fragment key={setting.name}>
                    <NotificationToggle setting={setting} />
                    <Divider />
                </Fragment>
            ))}
        </Stack>
    )
}
